import * as pages from "./pages/index";
import { useEffect, useState } from "react";
import { Stack, StackItem } from "@fluentui/react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { FluentProvider } from "@fluentui/react-components";
import SuperTokens, { SuperTokensWrapper } from "supertokens-auth-react";
import SessionProvider from "./components/session/sessionProvider";
import AuthCallbackView from "./api/sing_in/thirdPartyAuth";
import Header from "./components/header/header.component";
import { SuperTokenConfig } from "./Authentication/supertokenconfig";
import { Sidebar } from "./components/side_menu/sidebar.component";
import { GlobalContextProvider } from "./hooks/globalContext";
import { usePermissionStore } from "./Store/permissionStore";
import { PrivateRoutes } from "./privateroute"
import { Forbidden } from "./Forbidden";
import { appHeader, appBody, dividerWidthStyle, parentHome, sidenavTheme, sidebarTheme } from "./styles/customStyles";
// import getSuperTokensRoutesForReactRouterDom from 'supertokens-auth-react';

SuperTokens.init(SuperTokenConfig); // Supertoken init functions

export default function App() {
  const [layoutCondition, setLayoutCondition] = useState(false)
  const [leftOpen, setLeftOpen] = useState(true);
  const [showSidebar] = useState(true);
  const { permissions } = usePermissionStore();
  const handleFixedLayout = (value: boolean | ((prevState: boolean) => boolean)) => setLayoutCondition(value);

  console.log("Checking permission from app: ", permissions);
  useEffect(() => {
    console.log("Checking redirection-outside-if...");
    if (window.location.pathname === '/') {
      console.log("Checking redirection-inside-if...");
      window.location.assign('/Dashboard');
    };
  });

  const noPage = <Navigate to='/unauthorised' />;
  const setPermit = (key: string) => (permissions.includes("all") || permissions.some((e) => e.includes(key)));

  const routes = [
    { path: "/", element: <pages.Signin layoutCondition={layoutCondition} /> },
    { path: "/signup", element: <pages.Signup /> },
    { path: "/auth/reset-password", element: <pages.PasswordReset /> },
    { path: "/forbidden", element: <Forbidden /> },
    { path: "/verifyotp", element: <pages.Verifyotp /> },
    { path: "/auth/callback/:provider", element: <AuthCallbackView /> },
    {
      path: "",
      element: <SessionProvider> <PrivateRoutes handleFixedLayout={handleFixedLayout} /> </SessionProvider>,
      children: [
        { path: "/auth/change-password", element: <pages.ChangePassword /> },
        { path: "/home", element: <pages.Home /> },
        /** @DEVICES_PAGES */
        {
          path: "/manage/devices/*",
          children: [
            { path: "overview", element: setPermit("overview") ? <pages.Census /> : noPage },
            {
              path: "device_classification/*",
              children: [
                { path: "", element: setPermit("deviceClassification") ? <pages.Groups /> : noPage },
                { path: "devices", element: <pages.DeviceClassificationDevices /> },
                { path: "scores", element: <pages.DeviceClassificationScores /> },
                { path: "audit_log", element: <pages.DeviceClassificationAuditlog /> }
              ]
            },
            {
              path: "monitor/*",
              children: [
                { path: "", element: <pages.Monitor /> },
                { path: "assignment/:id", element: <pages.Monitor_Attach_Detach /> }
              ]
            },
            {
              path: "remediation/*",
              children: [
                { path: "", element: <pages.Remediation /> },
                { path: "assignment", element: setPermit("remediation") ? <pages.Remediation_Attach_Detach /> : noPage },
                { path: "activityLog", element: setPermit("remediation") ? <pages.Remediation_Activity_log /> : noPage }
              ]
            },
            {
              path: "compliancepolicies/*",
              children: [
                { path: "", element: <pages.Remediation /> },
                { path: "assignment", element: setPermit("compPolicies") ? <pages.Compliance_Attach_Detach /> : noPage }
              ]
            },
            {
              path: "conditionalaccess/*",
              children: [
                { path: "", element: <pages.Remediation /> },
                { path: "assignment", element: setPermit("conditionalAccess") ? <pages.ConditionalAccess_Attach_Detach /> : noPage }
              ]
            },
            {
              path: "usersurvey/*",
              children: [
                { path: "", element: <pages.Remediation /> },
                { path: "assignment", element: setPermit("userSurveys") ? <pages.Usersurveys_Attach_Detach /> : noPage }
              ]
            },
            {
              path: "datacollection/*",
              children: [
                { path: "", element: <pages.DataCollection /> },
                { path: "assignment", element: setPermit("dataCollection") ? <pages.Remediation_Attach_Detach /> : noPage }
              ]
            },
            { path: "compliance_policies", element: <pages.Compliance_Policies /> },
            { path: "activitylog", element: <pages.Activity_log /> },
            { path: "compliance_status", element: <pages.ComplianceStatus /> },
            /*
              { path: "compliancepolicies", element: <CompliancePolicies /> },
              { path: "conditionalaccess", element: <ConditionalAccess/> },
              { path: "usersurvey", element: <Usersurvey/> }
            */
          ]
        },
        /** @MANAGE_APPS_PAGES */
        {
          path: "/manage/apps/*",
          children: [
            { path: "appinstallstatus", element: <pages.Appinstallstatus /> },
            { path: "appuninstallstatus", element: <pages.Appuninstallstatus /> },
            { path: "appstatus", element: <pages.AppStatus /> },
            {
              path: "discoveredapps/*",
              children: [
                { path: "", element: <pages.Discoveredapps /> },
                { path: "discoveredapps_list", element: <pages.Discoveredapps_list /> }
              ]
            },
            { path: "activitylogg", element: <pages.Activitylog_Manage /> },
            /*
              { path: "appmonitor", element: <Appmonitored/> },
              { path: "appmonitored", element: <Appmonitored/> }
            */
          ]
        },
        /** @PATCH_MANAGEMENT_PAGES */
        { path: "/manage/patchmanagement/patchdetection", element: <pages.PatchDetection /> },
        /** @TROUBLESHOOT_PAGES */
        {
          path: "/manage/troubleshoot/*",
          children: [
            { path: "troubleshoot", element: <pages.TroubleShoot /> },
            { path: "status", element: <pages.Status /> },
            { path: "alert", element: <pages.Alert /> },
            { path: "compliance_policy", element: <pages.Compliance_Policy /> },
            { path: "alert/devices", element: <pages.Alert_Devices /> },
          ]
        },
        /** @SEARCH_PAGES */
        {
          path: "/manage/search/*",
          children: [
            {
              path: "asset/*",
              children: [
                { path: "", element: <pages.Asset /> },
                { path: "results", element: <pages.SubAssets /> }
              ]
            },
            {
              path: "event/*",
              children: [
                { path: "", element: <pages.Event /> },
                { path: "results", element: <pages.SubEvent /> }
              ]
            },
            /*
              {
                path: "alert/*",
                children: [
                  { path: "", element: <pages.Alert /> },
                  { path: "devices", element: <pages.Alert_Devices /> }
                ]
              },
              { path: "compliance_policy", element: <Compliance_Policy/> },
            */
          ]
        },
        /** @ADMINISTARTION_PAGES */
        {
          path: "/manage/administration/*",
          children: [
            { path: "role", element: <pages.Roles /> },
            { path: "user", element: <pages.Users /> },
            { path: "tenant", element: <pages.Tenant /> },
            { path: "SSO", element: <pages.SSOConfiguration /> },
            { path: "approvals", element: <pages.Approvals /> },
            {
              path: "upload/*",
              children: [
                { path: "", element: <pages.Uploader /> },
                { path: "fileupload", element: <pages.FileUploader /> }
              ]
            },
            { path: "version_update", element: <pages.VersionUpdate /> },
            { path: "activityLog", element: <pages.ActivityLogAdmin /> },
            { path: "endpoint", element: <pages.Endpoint /> }
          ]
        },
        /** @AUTOMATION_PAGES */
        {
          path: "/automate/automation/*",
          children: [
            {
              path: "all_automations/*",
              children: [
                { path: "", element: <pages.All_automation /> },
                { path: "cards", element: <pages.All_automation_cards /> }
              ]
            },
            { path: "configuration", element: <pages.Automation_config /> },
            { path: "autoheal", element: <pages.Autoheal /> },
            { path: "selfhelp", element: <pages.Selfhelp /> },
            { path: "automationschedule", element: <pages.AutomationSchedule /> },
            { path: "usersurvey", element: <pages.UserSurvey /> },
            { path: "compliancepolices", element: <pages.Compliance_policies /> },
            { path: "alerts", element: <pages.AutomateAlert /> },
            { path: "activitylog", element: <pages.ActivityLog /> },
            /*
              { path: "compliancepolicy", element: <pages.Policy_config/> },
            */
          ]
        },
        /** @AUTOMATE_APPS_PAGES */
        {
          path: "/automate/apps/*",
          children: [
            { path: "appinstallation", element: <pages.Appinstallation /> },
            { path: "appmonitored", element: <pages.Appmonitored /> },
            { path: "appuninstallprofile", element: <pages.Appuninstallprofile /> },
            { path: "appprotectionpolicies", element: <pages.Appprotectionpolicies /> },
            { path: "activitylog", element: <pages.Activitylog_Automate /> }
          ],
        },
        /** @SECURITY_PAGES */
        {
          path: "/automate/security/*",
          children: [
            { path: "compliancepolicy", element: <pages.Policy_config /> },
            { path: "data_collection", element: <pages.Data_collection_config /> },
            { path: "activitylog", element: <pages.SecurityModuleActivityLog /> },
          ]
        },
        /** @INVESTIGATE_PAGES */
        { children: [{ path: "/investigate/analytics", element: <pages.Analytics /> }] },
        { children: [{ path: "/unauthorised", element: <pages.Unauthorised /> }] },
        { children: [{ path: "/restricted", element: <pages.Restricted /> }] },
        /** @USER_PAGES */
        /*
          {
            path: "/manage/user/*",
            children: [
              { path: "role", element: <Role/> },
              { path: "alluser", element: <AllUsers/> },
              { path: "activitylog", element: <ActivityLogs/> }
            ]
          }
        */
      ]
    }
  ];

  function dashboardRoutes(routes: any) {
    return routes.map(({ path, element, children }: any, index: number) => (
      <Route key={index} path={path} element={element}> {children ? dashboardRoutes(children) : null} </Route>
    ));
  };

  return (
    <GlobalContextProvider>
      <SuperTokensWrapper>
        <BrowserRouter basename="/Dashboard">
          <Stack className="App" style={parentHome}>
            <Stack className="App" tokens={{ childrenGap: 40 }} style={parentHome}>
              {layoutCondition && <Stack className="header" style={appHeader}> <Header /> </Stack>}
              <Stack style={layoutCondition ? { height: 'calc(100% - 40px)' } : parentHome}>
                <Stack className="Container" style={appBody}>
                  <Stack horizontal style={parentHome}>
                    <FluentProvider theme={sidenavTheme} style={{ ...parentHome, ...sidebarTheme }}>
                      {showSidebar ? layoutCondition && <Sidebar leftOpen={leftOpen} setLeftOpen={setLeftOpen} /> : <Navigate to="/" />}
                    </FluentProvider>
                    <StackItem style={{ ...dividerWidthStyle }}> <Routes> {dashboardRoutes(routes)} </Routes> </StackItem>
                  </Stack>
                </Stack>
              </Stack>
            </Stack>
          </Stack>
        </BrowserRouter>
      </SuperTokensWrapper>
    </GlobalContextProvider>
  );
};
